/**
 * src/shell/portal/components/QuotePostModal.tsx
 *
 * 1-Tap Re-Wall and Quote Post Modal for Sovereign Walls.
 * Allows users to mirror channel or wall posts to their personal wall
 * either as a clean re-wall or with added commentary.
 */

import React, { useState } from 'react';
import { Repeat, MessageSquare, X, Send, ShieldCheck, Check } from 'lucide-react';
import type { SocialPost } from '../../../domain/social/channel-contracts.js';
import type { WallPostRecord } from '../../../domain/social/wall-contracts.js';

export interface QuotePostModalProps {
  sourcePost: SocialPost | WallPostRecord;
  sourceChannelName: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmitReshare: (params: { isQuote: boolean; commentary?: string }) => void;
}

export const QuotePostModal: React.FC<QuotePostModalProps> = ({
  sourcePost,
  sourceChannelName,
  isOpen,
  onClose,
  onSubmitReshare,
}) => {
  const [commentary, setCommentary] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<'REPOST' | 'QUOTE'>('QUOTE');

  if (!isOpen) return null;

  const authorHandle = 'authorHandle' in sourcePost ? sourcePost.authorHandle : (sourcePost as any).authorHandle || '@creator';
  const authorDisplayName = 'authorDisplayName' in sourcePost ? sourcePost.authorDisplayName : authorHandle;
  const contentText = 'text' in sourcePost && sourcePost.text ? sourcePost.text : 'contentMarkdown' in sourcePost ? (sourcePost as any).contentMarkdown : '';

  const handleAction = () => {
    setIsSubmitting(true);
    try {
      if (mode === 'REPOST') {
        onSubmitReshare({ isQuote: false });
      } else {
        onSubmitReshare({ isQuote: true, commentary: commentary.trim() });
      }
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', padding: '16px' }}>
      <div style={{ width: '100%', maxWidth: '480px', backgroundColor: 'var(--theme-bg-modal, #0f172a)', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '16px', padding: '20px', color: 'var(--theme-text-primary, #f8fafc)', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', position: 'relative', overflow: 'hidden' }}>

        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '12px', marginBottom: '16px', borderBottom: '1px solid var(--theme-border, #1e293b)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--theme-accent, #38bdf8)' }}>
              <Repeat size={16} style={{ flexShrink: 0 }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--theme-text-primary, #f8fafc)' }}>Add to My Wall</h3>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>Publish to your sovereign profile feed</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--theme-text-muted, #94a3b8)', cursor: 'pointer', fontSize: '16px', padding: '4px' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Mode Selector */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px', padding: '4px', borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--theme-border, #1e293b)' }}>
          <button
            type="button"
            onClick={() => setMode('QUOTE')}
            style={{
              padding: '8px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer',
              border: 'none',
              backgroundColor: mode === 'QUOTE' ? 'var(--theme-accent, #38bdf8)' : 'transparent',
              color: mode === 'QUOTE' ? '#0b0f19' : 'var(--theme-text-muted, #94a3b8)',
            }}
          >
            <MessageSquare size={13} style={{ flexShrink: 0 }} /> Quote with Note
          </button>
          <button
            type="button"
            onClick={() => setMode('REPOST')}
            style={{
              padding: '8px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer',
              border: 'none',
              backgroundColor: mode === 'REPOST' ? 'var(--theme-accent, #38bdf8)' : 'transparent',
              color: mode === 'REPOST' ? '#0b0f19' : 'var(--theme-text-muted, #94a3b8)',
            }}
          >
            <Repeat size={13} style={{ flexShrink: 0 }} /> Instant Re-Wall
          </button>
        </div>

        {/* Quote Commentary Input */}
        {mode === 'QUOTE' && (
          <div style={{ marginBottom: '16px' }}>
            <textarea
              value={commentary}
              onChange={(e) => setCommentary(e.target.value)}
              placeholder="What are your thoughts on this? Add your take..."
              rows={3}
              maxLength={500}
              style={{
                width: '100%',
                padding: '10px 12px',
                backgroundColor: 'var(--theme-bg-input, #1e293b)',
                border: '1px solid var(--theme-border, #1e293b)',
                borderRadius: '10px',
                fontSize: '12px',
                color: 'var(--theme-text-primary, #f8fafc)',
                outline: 'none',
                resize: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}

        {/* Embedded Original Post Preview */}
        <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--theme-border, #1e293b)', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--theme-text-primary, #f8fafc)' }}>{authorDisplayName}</span>
            <span style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', fontFamily: 'var(--theme-font-mono)' }}>{authorHandle}</span>
            <span style={{ fontSize: '10px', color: 'var(--theme-accent, #38bdf8)', backgroundColor: 'rgba(56, 189, 248, 0.12)', padding: '2px 6px', borderRadius: '9999px', fontFamily: 'var(--theme-font-mono)' }}>
              in {sourceChannelName}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--theme-text-secondary, #cbd5e1)', lineHeight: 1.4 }}>
            {contentText}
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid var(--theme-border, #1e293b)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', fontFamily: 'var(--theme-font-mono)' }}>
            <ShieldCheck size={14} color="#34d399" style={{ flexShrink: 0 }} />
            <span>Canonical Provenance Tracked</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--theme-text-secondary, #cbd5e1)',
                backgroundColor: 'transparent',
                border: '1px solid var(--theme-border, #1e293b)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isSubmitting || (mode === 'QUOTE' && !commentary.trim())}
              onClick={handleAction}
              style={{
                padding: '6px 16px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 700,
                color: '#0b0f19',
                backgroundColor: mode === 'QUOTE' && !commentary.trim() ? '#64748b' : 'var(--theme-accent, #38bdf8)',
                border: 'none',
                cursor: mode === 'QUOTE' && !commentary.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>{isSubmitting ? 'Posting...' : mode === 'REPOST' ? 'Re-Wall' : 'Post Quote'}</span>
              <Send size={12} style={{ flexShrink: 0 }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
